/**
 * Mounts Search on `[data-component="search"]`.
 * Matrix template outputs URI-encoded JSON on `data-props` (see `src/matrix/shared/search.html`).
 */
import { getOrCreateRoot } from '../../utils/getOrCreateRoot';
import { parseShowHide } from '../../utils/parse-show-hide';
import type { SearchDataProps } from '../../types';
import Search from '.';
import { SearchAppProvider } from '../../context/search-app/search-app-context';

function parseBoolean(value: unknown, fallback: boolean): boolean {
  return parseShowHide(value, fallback);
}

function parsePageMetadataFilters(
  raw: unknown,
): SearchDataProps['pageMetadataFilters'] | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const topic =
    typeof o['dxp-topic'] === 'string' ? o['dxp-topic'] : undefined;
  const faculty =
    typeof o['dxp-faculty'] === 'string' ? o['dxp-faculty'] : undefined;
  if (!topic && !faculty) {
    return undefined;
  }
  return {
    ...(topic ? { 'dxp-topic': topic } : {}),
    ...(faculty ? { 'dxp-faculty': faculty } : {}),
  };
}

export function parseSearchDataProps(raw: string | null): SearchDataProps {
  if (!raw?.trim()) {
    return {
      pageUrlPath: '',
      showFacets: true,
      showTextSearch: true,
      defaultQuery: '',
    };
  }
  try {
    const decoded = decodeURIComponent(raw);
    const parsed = JSON.parse(decoded) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return {
        pageUrlPath: '',
        showFacets: true,
        showTextSearch: true,
        defaultQuery: '',
      };
    }
    const o = parsed as Record<string, unknown>;
    return {
      pageUrlPath: typeof o.pageUrlPath === 'string' ? o.pageUrlPath : '',
      showFacets: parseBoolean(o.showFacets, true),
      showTextSearch: parseBoolean(o.showTextSearch, true),
      defaultQuery:
        typeof o.defaultQuery === 'string' ? o.defaultQuery : '',
      pageMetadataFilters: parsePageMetadataFilters(o.pageMetadataFilters),
    };
  } catch {
    return {
      pageUrlPath: '',
      showFacets: true,
      showTextSearch: true,
      defaultQuery: '',
    };
  }
}

const initSearchReactApp = () => {
  const wrapper = document.querySelector('[data-component="search"]');
  if (!wrapper) return;

  const dataProps = wrapper.getAttribute('data-props');
  const { pageUrlPath, defaultQuery, showFacets, showTextSearch, pageMetadataFilters } =
    parseSearchDataProps(dataProps);

  try {
    getOrCreateRoot(wrapper).render(
      <SearchAppProvider
        pageUrlPath={pageUrlPath}
        defaultQuery={defaultQuery}
        showFacets={showFacets}
        showTextSearch={showTextSearch}
        pageMetadataFilters={pageMetadataFilters}
      >
        <Search />
      </SearchAppProvider>,
    );
    wrapper.removeAttribute('data-props');
  } catch (e) {
    console.error('Search: failed to parse JSON or render', e);
  }
};

document.addEventListener('DOMContentLoaded', () => {
  initSearchReactApp();
});

document.addEventListener('livePreviewUpdated', () => {
  setTimeout(() => {
    initSearchReactApp();
  }, 1000);
});
